import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { ICONS } from '../../../lib/ctdIcons';
import { formatErrorToString, getTestProps } from '../../../lib/helpers';

import Card from '../../../components/Card/Card';
import Tooltip from '../../../components/Tooltip/Tooltip';

import { QuestionMarkCircleIcon } from '../../../images/shapes';

import { TRANSLATIONS_KEYS } from '../PropertyModalFormContent/PropertyModalFormContent';

interface PropertyCardProps {
  /**
   * Property input type
   */
  inputType: string;
  /**
   * On card click callback
   */
  onClick?: (inputType: string) => void;
  /**
   * Property validation errors
   */
  propertyErrors?: {
    key: string | string[];
    config: Record<string, string | string[]>;
    schema: Record<string, string | string[]>;
    required: string | string[];
  };
  /**
   * Test id for property card
   */
  testId?: string;
}

const PropertyCard = ({
  inputType,
  onClick,
  propertyErrors,
  testId,
}: PropertyCardProps) => {
  const { t } = useTranslation();

  const onCardClick = useCallback(
    () => onClick?.(inputType),
    [onClick, inputType],
  );

  const cardLabel = useMemo(
    () => (
      <p className="text-slate-900 dark:text-white text-lg font-medium">
        {t(
          `PropertyForm.Labels.${
            inputType in TRANSLATIONS_KEYS
              ? TRANSLATIONS_KEYS[inputType].key
              : inputType
          }`,
        )}
      </p>
    ),
    [t, inputType],
  );

  return (
    <div>
      <Card
        onClick={onClick ? onCardClick : undefined}
        thumbnail={
          inputType in ICONS
            ? ICONS[inputType as keyof typeof ICONS]
            : undefined
        }
        body={
          <div className="flex flex-row text-slate-400 h-full justify-between items-center mt-2">
            <div className="dark:text-white">
              {inputType in TRANSLATIONS_KEYS &&
              TRANSLATIONS_KEYS[inputType].tooltipKey ? (
                <Tooltip
                  tooltip={t(
                    `PropertyForm.Tooltips.${TRANSLATIONS_KEYS[inputType].key}`,
                  )}
                  tooltipPlacement="topLeft"
                  phoneTooltipPlacement="topLeft"
                  additionalClasses="w-fit inline-flex"
                >
                  {cardLabel}
                  <QuestionMarkCircleIcon className="text-blue w-3 ml-1" />
                </Tooltip>
              ) : (
                cardLabel
              )}
              <div className="mt-1 dark:text-slate-400">
                {t(
                  `PropertyForm.Descriptions.${TRANSLATIONS_KEYS[inputType].key}`,
                )}
              </div>
            </div>
            <div className="flex flex-col xs:flex-row items-center gap-2">
              <div className="inline-flex items-center gap-2"></div>
            </div>
          </div>
        }
        darkenOnHover={false}
        additionalContainerClasses={twMerge(
          'p-4 bg-slate-50 dark:bg-slate-800 w-full h-full overflow-visible',
          propertyErrors?.schema?.type && 'border border-red',
        )}
        additionalImageContainerClasses="w-fit h-fit rounded-sm"
        additionalBodyClasses="w-full"
        additionalThumbnailClasses="w-fit h-fit"
        {...getTestProps(testId, `${inputType}`, 'testId')}
      />
      {propertyErrors?.schema?.type && (
        <div
          className="text-red text-sm mb-2 mt-1"
          {...getTestProps(testId, 'schema-type-error')}
        >
          {formatErrorToString(propertyErrors?.schema?.type)}
        </div>
      )}
    </div>
  );
};

export default PropertyCard;
